<template>
    <div class="table-container w-full">
        <div class="list-top-container">
            <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                <a-input-search
                    data-cy="listmanager-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="w-1/4" data-cy="admin-settings-list-association-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    @change="handleFilterChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in association_list" :key="index" :value="index">
                        {{ value.name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="w-1/4" data-cy="admin-settings-list-category-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter_category')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption2"
                    @change="handleFilterCategoryChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in categories" :key="index" :value="index">
                        {{ value.text }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="list-buttons w-full mb-2 md:w-auto md:mb-0">
                <a-dropdown :trigger="['click']" v-if="recordsSelected" class="mr-4">
                    <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                        {{ $t('admin.list_manager.table.buttons.mass_actions') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onActionButtons">
                            <a-menu-item key="delete" data-cy="actions-delete-item">
                                {{ $t('admin.list_manager.table.buttons.delete') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
                <a-button-group>
                    <a-button @click="onAdd" data-cy="btn-create-item" type="primary">
                        {{ $t('admin.list_manager.table.buttons.add') }}
                    </a-button>
                </a-button-group>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="list-manager-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="{ selectedRowKeys: selected_row_keys, onChange: onSelectChange }"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('admin.list_manager.table.buttons.edit')">
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'edit')"
                                        :disabled="recordsSelected"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('admin.list_manager.table.buttons.delete')">
                                    <a-button
                                        class="text-default-red hover:text-default-red focus:text-default-red"
                                        data-cy="btn-item-delete-single"
                                        @click="onTableAction(record.id, 'delete')"
                                        :disabled="recordsSelected"
                                    >
                                        <DeleteOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'code'">
                            {{ codeContent(record) }}
                        </template>
                        <template v-else-if="column.key === 'is_active'">
                            <CheckOutlined v-if="record.is_active" />
                            <CloseOutlined v-if="!record.is_active" />
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { CheckOutlined, CloseOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        CheckOutlined,
        CloseOutlined,
        DeleteOutlined,
        DownOutlined,
        EditOutlined,
    },
    data() {
        return {
            data: [],
            search_value: '',
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            list_manager_item: {},
            association_id: null,
            association_list: {},
            category: null,
            categories: [],
        };
    },
    props: {
        selected_row_keys: Array,
    },
    mounted() {
        this.fetch();

        this.$http
            .get('/api/admin/list-associations')
            .then((response) => {
                this.association_list = response.data.data;
            })
            .catch((error) => {
                this.$notification.error({
                    message: this.$t('globals.global.notifications.error.any', { code: 'CVAL02' }),
                });
            });

        this.$http.get('/api/listmanagermeta/categories').then((response) => {
            this.categories = [];

            response.data.data.forEach((record) => {
                this.categories.push({
                    text: record.list_name,

                    value: record.category,
                });
            });
        });
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.list_manager.headers.code'),
                    key: 'code',
                },
                {
                    title: this.$t('globals.tables.list_manager.headers.category'),
                    key: 'category',
                    dataIndex: 'category',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.list_manager.headers.item'),
                    key: 'item',
                    dataIndex: 'item',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.list_manager.headers.value'),
                    key: 'value',
                    dataIndex: 'value',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.list_manager.headers.description'),
                    key: 'description',
                    dataIndex: 'description',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.list_manager.headers.is_active'),
                    key: 'is_active',
                    dataIndex: 'is_active',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
    },
    methods: {
        fetch(params = {}) {
            this.loading = true;
            this.params = {
                ...this.params,
                ...params,
            };

            this.$http
                .get('/api/listmanager', { params: this.params })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVLMIF01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        codeContent(record) {
            let codes = '';

            if (
                record.association_code !== null &&
                record.company_code !== null &&
                record.association_code !== '' &&
                record.company_code !== ''
            ) {
                codes = record.association_code + '-' + record.company_code;
            } else if (record.association_code !== null && record.association_code !== '') {
                codes = record.association_code;
            } else if (record.company_code !== null && record.company_code !== '') {
                codes = record.company_code;
            }

            return codes;
        },
        onSelectChange(selected_row_keys, selected_rows) {
            this.$emit('onSelectChange', selected_row_keys);
        },
        onTableAction(id, key) {
            switch (key) {
                case 'edit':
                    this.$emit('onTableAction', {
                        id: id,
                        key: key,
                    });
                    break;
                case 'delete':
                    this.onDelete(id);
                    break;
            }
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.search_value = input;
                this.onSelectChange([]);

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleFilterChange(value) {
            if (value == undefined) {
                this.association_id = null;
            } else {
                this.association_id = this.association_list[value].id;
            }

            this.onSelectChange([]);

            this.fetch({
                association_id: this.association_id,
                search_value: this.search_value,
            });
        },
        handleFilterCategoryChange(value) {
            if (value == null) {
                this.category = null;
            } else {
                this.category = this.categories[value].value;
            }

            this.onSelectChange([]);

            this.fetch({
                association_id: this.association_id,
                search_value: this.search_value,
                category: this.category,
            });
        },
        filterOption(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        filterOption2(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        onAdd() {
            this.$emit('onTableAction', {
                id: null,
                key: 'edit',
            });
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'delete':
                    this.onDelete();
                    break;
            }
        },
        onDelete(item_id = '') {
            let ids = [];

            if (item_id !== '' && item_id !== undefined) {
                ids = [item_id];
            } else if (this.selected_row_keys.length >= 1) {
                ids = this.selected_row_keys;
            }

            let that = this;

            this.$confirm({
                title: this.$t('admin.list_manager.confirm.delete'),
                content: '',
                onOk() {
                    that.$http
                        .post('/api/listmanager/delete', {
                            ids: ids,
                        })
                        .then((response) => {
                            if (response.data.status == 'success') {
                                that.$notification.success({
                                    message: response.data.message,
                                });

                                that.$emit('onSelectChange', []);

                                that.reload();
                            } else {
                                that.$notification.error({
                                    message: response.data.message,
                                });
                            }
                        })
                        .catch((error) => {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVLMID01' }),
                            });
                        })
                        .finally(() => {
                            that.processing = false;
                        });
                },
            });
        },
    },
};
</script>
